import React, { useMemo, memo } from 'react';
import classNames from 'classnames';
import { formatInTimeZone } from 'date-fns-tz';
import { Box, Typography } from '@mui/material';
import { oneMinute } from 'utils/time';
import type { RewindGraphGridParams } from '../hooks/useRewindGraphGridParams';

import styles from './RewindGraphTimeAxis.module.scss';

const TIME_AXIS_TICK_SPACING_IN_MINUTES = 5;

export const RewindGraphTimeAxis = ({
  bucketStartTimeInMs,
  bucketEndTimeInMs,
  timezone,
  graphGridParameters,
  tickSpacingInMs = TIME_AXIS_TICK_SPACING_IN_MINUTES * oneMinute,
  numberOfMinorTicks = 0,
  axisDateLabelFormat = 'h:mma',
}: {
  bucketStartTimeInMs: number;
  bucketEndTimeInMs: number;
  tickSpacingInMs: number;
  timezone: string;
  graphGridParameters: RewindGraphGridParams;
  axisDateLabelFormat?: string;
  numberOfMinorTicks?: number;
}) => {
  const numberOfTicksPerMajorTick = numberOfMinorTicks + 1;

  const timeAxisLabels = useMemo(() => {
    const rewindLengthInMs = bucketEndTimeInMs - bucketStartTimeInMs;
    const numberOfLabels =
      Math.floor(rewindLengthInMs / tickSpacingInMs) * numberOfTicksPerMajorTick + 1;

    const gridColumnWidthInMs = Math.floor(
      rewindLengthInMs / graphGridParameters.chartAreaColumnsLength,
    );

    const tickSpacingInColumns = Math.round(
      tickSpacingInMs / gridColumnWidthInMs / numberOfTicksPerMajorTick,
    );

    const axisLabels = Array(numberOfLabels)
      .fill(0)
      .map((_, index) => ({
        timeInMs: bucketStartTimeInMs + index * (tickSpacingInMs / numberOfTicksPerMajorTick),
        index: index * tickSpacingInColumns,
        date: new Date(bucketStartTimeInMs + index * (tickSpacingInMs / numberOfTicksPerMajorTick)),
      }));

    return axisLabels;
  }, [graphGridParameters, bucketStartTimeInMs, bucketEndTimeInMs, tickSpacingInMs]);

  return (
    <>
      {timeAxisLabels.map((time, index) => {
        const isMajorTick = index % numberOfTicksPerMajorTick === 0;

        return (
          <React.Fragment key={time.timeInMs}>
            <Box
              data-testid="rewinds-time-axis-ticks"
              className={classNames({
                [styles.timeAxisTicks]: isMajorTick,
                [styles.timeAxisMinorTicks]: !isMajorTick,
              })}
              gridColumn={graphGridParameters.chartStartColumn + time.index}
              gridRow={graphGridParameters.timeAxisTicksRow}
            />
            {isMajorTick && (
              <Typography
                data-testid="rewinds-time-axis-labels"
                className={styles.timeAxisLabels}
                variant="caption2"
                gridColumn={`${Math.max(
                  1,
                  graphGridParameters.chartStartColumn + time.index - 2,
                )} / ${graphGridParameters.chartStartColumn + time.index}`}
                gridRow={graphGridParameters.timeAxisLabelsRow}
              >
                {formatInTimeZone(time.date, timezone, axisDateLabelFormat).toLowerCase()}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default memo(RewindGraphTimeAxis);
